import { Provider } from 'jotai';
import React from 'react';

import { CategoryOption } from 'api/api';

import { CategoryAddressProvider } from '~src/address/search-address/address-provider';
import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { DeliveryTime } from '~src/delivery-time/delivery-time';
import { useSessionState } from '~src/global-state';

import { useHasAddress } from '~address-form/utils/address-utils';

import { CategoryExtensionProps } from '../category/category-manager';
import { useCategoryState } from '../category/category-state';
import { constructUserChoice } from '../category/user-choice';
import { UserChoiceProvider, UserChoiceProviderChildProps } from '../category/user-choice-provider';
import { LogoDeck } from '../commons/components/logo-deck-row';
import { FullscreenComponent, FullscreenMode } from '../fullscreen-internals/fullscreen-mode';
import { PriceComponent } from '../price/price';
import { colors } from '../theme/colors';
import { AriaGrid, useAriaGridCell } from '../ui/aria-grid';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { ListState, useListState } from '../ui/list-state';
import { useScrollIntoView } from '../ui/scroll-into-view';
import { ShippingCardUI } from '../ui/shipping-card';
import { InlineHomeDevlieryExtension } from './inline-home-delivery-extension';
import { LocationModalLayout } from './location-modal-layout';

interface HomeDeliveryListItemProps {
  option: CategoryOption;
  listState: ListState<CategoryOption>;
}

export const HomeDeliveryListItem: React.FC<HomeDeliveryListItemProps> = ({
  option,
  listState,
}) => {
  const { tICU } = useTranslations();
  const category = useCategoryState();

  const ref = React.useRef(null);

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: option.shipping_method,
    listState,
    ref,
    enableAutoSelect: true,
  });

  useScrollIntoView(ref, isSelected);

  return (
    <ShippingCardUI
      disableMobileIndent
      headerExtensions={{
        price: <PriceComponent value={option.price} />,
        /*
         * Warning: Some merchants have a setup to translate a shipping_method to a
         * different carrier logo. Be careful with this as this is rarely used and
         * an easy place for regression.
         */
        logo: <LogoDeck shippingMethods={[option.carrier_logo || option.shipping_method]} />,
        subtitle: <DeliveryTime deliveryTime={option.delivery_times[0]} type="delivery" />,
      }}
      radioTitle={
        isSelected ? tICU(`widget.aria.SELECTED_HOME_DELIVERY`) : tICU(`widget.aria.HOME_DELIVERY`)
      }
      key={option.shipping_method}
      selected={isSelected}
      title={option.carrier}
      reserveLogoSpace={category.flags.show_carrier_logos}
      spacing="dense"
      innerRef={ref}
      {...cellProps}
    />
  );
};

interface HomeDeliveryModalProps {
  onClose: () => void;
  onSubmit: (option: CategoryOption) => void;
  initialSelectedOption: CategoryOption;
}

export const HomeDeliveryModal: FullscreenComponent<HomeDeliveryModalProps> = ({
  initialSelectedOption,
  onClose,
  onSubmit,
}) => {
  const category = useCategoryState();
  const listState = useListState({
    items: category.options,
    getKey: (option: CategoryOption) => option.shipping_method,
    defaultSelectedKey: initialSelectedOption.shipping_method,
  });

  const { tICU } = useTranslations();

  const listElement = (
    <AriaGrid listState={listState}>
      {(option: CategoryOption) => (
        <HomeDeliveryListItem key={option.shipping_method} option={option} listState={listState} />
      )}
    </AriaGrid>
  );

  return (
    <Dialog height={600} width={520} onClose={onClose}>
      <LocationModalLayout
        title={tICU('widget.SELECT_CARRIER')}
        listElement={listElement}
        buttonElement={
          <Button type="submit" onClick={() => onSubmit(listState.getSelectedItem()!)}>
            {tICU('widget.SELECT_BUTTON')}
          </Button>
        }
        onClose={onClose}
      />
    </Dialog>
  );
};
HomeDeliveryModal.fullscreenName = 'HomeDeliveryModal';

export const HomeDeliveryExtension: React.FC<CategoryExtensionProps> = ({
  selected,
  selectedOption,
}) => {
  const category = useCategoryState();
  const hasAddress = useHasAddress();
  const session = useSessionState();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const { tICU } = useTranslations();

  if (
    !selected ||
    (category.delivery_type !== 'delivery' && category.delivery_type !== 'mailbox') ||
    category.options.length < 2 ||
    !category.flags.enable_choice_selection
  ) {
    return null;
  }

  if (session.flags.enable_inline_category_extensions) {
    return (
      <Provider>
        <InlineHomeDevlieryExtension initialSelectedOption={selectedOption} />
      </Provider>
    );
  }

  const renderModal = ({ onUpdate }: UserChoiceProviderChildProps) => {
    if (!modalOpen) {
      return null;
    }

    return (
      <FullscreenMode>
        <HomeDeliveryModal
          initialSelectedOption={selectedOption}
          onClose={() => setModalOpen(false)}
          onSubmit={(newOption: CategoryOption) => {
            onUpdate(constructUserChoice(category, newOption));
            setModalOpen(false);
          }}
        />
      </FullscreenMode>
    );
  };

  return (
    <>
      <CategoryAddressProvider onUpdateAddress={() => setModalOpen(true)}>
        {({ onOpen }) => (
          <OpenModalButton
            title={{
              text: hasAddress ? selectedOption.carrier : tICU('widget.SELECT_CARRIER'),
              textStyle: hasAddress ? 'bold' : 'normal',
              color: hasAddress ? colors.black : colors.neutral35,
            }}
            data-test="home-delivery-button"
            onClick={hasAddress ? () => setModalOpen(true) : onOpen}
            ariaLabel={tICU('widget.SELECT_CARRIER')}
          />
        )}
      </CategoryAddressProvider>

      <UserChoiceProvider>{renderModal}</UserChoiceProvider>
    </>
  );
};
